import { useEffect, useRef, useState } from "react";
import { Check, Type } from "lucide-react";
import {
  FONT_SIZE_MAP,
  useFontSize,
  type FontSize,
} from "@/hooks/useFontSize";

const BORDER = "hsl(210,30%,85%)";
const PRIMARY = "hsl(210,68%,55%)";
const ACTIVE_BG = "hsl(210,55%,96%)";
const ACTIVE_BTN_BG = "hsl(210,55%,92%)";
const HOVER_BG = "hsl(210,30%,97%)";

const OPTIONS: { value: FontSize; label: string }[] = [
  { value: "small", label: "Small" },
  { value: "default", label: "Default" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

export function FontSizeButton() {
  const { size, setSize } = useFontSize();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function handleSelect(next: FontSize) {
    setSize(next);
    setOpen(false);
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Text Size"
        aria-label="Text Size"
        aria-expanded={open}
        className="flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
        style={
          open
            ? { background: ACTIVE_BTN_BG, color: PRIMARY }
            : { color: "hsl(215,16%,47%)" }
        }
      >
        <Type className="h-4 w-4" strokeWidth={1.5} />
      </button>

      {open && (
        <div
          className="absolute overflow-hidden rounded-lg bg-white"
          style={{
            top: "calc(100% + 8px)",
            right: 0,
            width: 160,
            border: `1px solid ${BORDER}`,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              padding: "8px 14px 6px",
              borderBottom: "1px solid hsl(210,30%,90%)",
            }}
          >
            <span
              className="font-semibold uppercase"
              style={{
                fontSize: 11,
                color: "hsl(215,16%,47%)",
                letterSpacing: "0.04em",
              }}
            >
              Text Size
            </span>
          </div>
          {OPTIONS.map((opt) => {
            const active = opt.value === size;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt.value)}
                className="flex w-full items-center justify-between transition-colors"
                style={{
                  height: 36,
                  padding: "0 14px",
                  background: active ? ACTIVE_BG : "transparent",
                  color: active ? PRIMARY : "hsl(215,25%,20%)",
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = HOVER_BG;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                <span style={{ fontSize: FONT_SIZE_MAP[opt.value] }}>
                  {opt.label}
                </span>
                {active && (
                  <Check
                    className="h-3.5 w-3.5"
                    strokeWidth={2.5}
                    style={{ color: PRIMARY }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
